import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { GET_CURRENT_USER } from '../../backend/graphql/queries';
import { QueryDescriptor, QueryType } from '../../graphql/queryDescriptors';
import { swrFetcher } from '../../services/swr';
import { StoreState } from '../../store/types';
import { UserManageable } from '../user/types';

export const useCustomQuery = (descriptor: QueryDescriptor) => {
  // State
  const [isLoading, setLoading] = useState<boolean>(false);

  // Hooks
  const dispatch = useDispatch();
  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);

  // Helper
  const getVariables = useCallback(
    (input?: any) => {
      return input ? input : descriptor.input ? descriptor.input(storageUserDetails) : undefined;
    },
    [descriptor, storageUserDetails]
  );

  const getName = useCallback(
    () => {
      let query = typeof descriptor.query === "string" ? descriptor.query : descriptor.query({});
      let name = query.split(' ')[2];
      return name ? name : "Unknown";
    },
    [descriptor]
  );

  const options = { refreshInterval: descriptor.pollInterval };

  const onError = useCallback(
    (
      rawError: any, variables?: any
    ) => {
      const error =
      rawError.response && rawError.response.errors && rawError.response.errors.length > 0 ? rawError.response.errors[0] :
        rawError.response && rawError.response.error ? rawError.response.error : { message: "Unknown" };

      console.log(
        '[' + getName() + '] - Error',
        {
          input: getVariables(variables),
          error: error,
          request: typeof descriptor.query === "string" ? descriptor.query : descriptor.query(getVariables(variables)),
        }
      );

      if (descriptor.onError) {
        descriptor.onError(
          error.message,
          dispatch
        );
      }
    },
    [descriptor, dispatch, getName, getVariables]
  );

  const onSuccess = useCallback(
    (
      data: any, variables?: any
    ) => {
      console.log(
        '[' + getName() + '] - Success',
        {
          input: getVariables(variables),
          output: data,
          request: typeof descriptor.query === "string" ? descriptor.query : descriptor.query(getVariables(variables)),
        }
      );

      if (descriptor.onSuccess) {
        descriptor.onSuccess(
          data,
          dispatch,
          storageUserDetails
        );
      }
    },
    [descriptor, dispatch, getName, getVariables, storageUserDetails]
  );

  const isExecutingQuery = useMemo(
    () => descriptor.type === QueryType.Query && (!descriptor.skip || !descriptor.skip(storageUserDetails)),
    [descriptor]
  );

  // Execute
  const {
    isValidating, mutate
  } = useSWR(
    GET_CURRENT_USER.content,
    () => {
      if (isExecutingQuery) {
        return swrFetcher(
          descriptor,
          getVariables()
        )
          .then((result) => {
            onSuccess(result);
          })
          .catch((error) => {
            onError(error);
          });
      }

      return undefined;
    },
    options,
  );

  let executeWrapper = useCallback(
    async (data?: any) => {
      setLoading(true);

      const variables = getVariables(data ? data.variables : undefined);
      const result = await swrFetcher(
        descriptor,
        variables
      )
        .then((result) => {
          onSuccess(
            result,
            variables
          );

          if (data.onSuccess) {
            data.onSuccess();
          }
        })
        .catch((error) => {
          onError(
            error,
            variables
          );
        });

      setLoading(false);

      mutate(
        result,
        false
      );
    },
    [descriptor, getVariables, mutate, onError, onSuccess]
  );

  return {
    execute: executeWrapper,
    loading: isValidating && isExecutingQuery || isLoading,
  };
}